/**
 * Recognise Google Drive / Docs, OneDrive and SharePoint URLs.
 *
 * These links point at files behind the provider's sign-in, so a server-side
 * unfurl (LinkPreviewCard) only ever fetches a login page. Instead we render a
 * provider card built from what the URL itself says: which provider, what kind
 * of file (/document/, /spreadsheets/, SharePoint's /:x:/ etc.), and the file
 * name when the path or query carries one.
 */

import type { CloudFileProvider } from '@/services/messagingApi';

export type CloudFileKind =
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'form'
  | 'drawing'
  | 'pdf'
  | 'image'
  | 'video'
  | 'archive'
  | 'folder'
  | 'file';

export interface CloudLinkInfo {
  provider: CloudFileProvider;
  kind: CloudFileKind;
  name: string | null;
  href: string;
}

export const PROVIDER_LABEL: Record<CloudFileProvider, string> = {
  google_drive: 'Google Drive',
  onedrive: 'OneDrive',
  sharepoint: 'SharePoint',
};

const EXT_KIND: Record<string, CloudFileKind> = {
  doc: 'document', docx: 'document', odt: 'document', rtf: 'document', txt: 'document', md: 'document',
  xls: 'spreadsheet', xlsx: 'spreadsheet', xlsm: 'spreadsheet', csv: 'spreadsheet', ods: 'spreadsheet',
  ppt: 'presentation', pptx: 'presentation', odp: 'presentation',
  pdf: 'pdf',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', heic: 'image', svg: 'image',
  mp4: 'video', mov: 'video', webm: 'video', avi: 'video',
  zip: 'archive', gz: 'archive', tar: 'archive', '7z': 'archive', rar: 'archive',
};

export function kindFromFilename(name: string | null | undefined): CloudFileKind | null {
  if (!name) return null;
  const m = /\.([a-z0-9]{1,5})$/i.exec(name.trim());
  return m ? EXT_KIND[m[1].toLowerCase()] ?? null : null;
}

/** File kind from a MIME type (incl. Google's application/vnd.google-apps.*), falling back to the name. */
export function kindFromMime(mime: string | null | undefined, name?: string | null): CloudFileKind {
  const m = (mime || '').toLowerCase();
  if (m.includes('folder')) return 'folder';
  if (m.includes('google-apps.document') || m.includes('wordprocessing') || m === 'application/msword') return 'document';
  if (m.includes('google-apps.spreadsheet') || m.includes('spreadsheet') || m.includes('ms-excel') || m === 'text/csv') return 'spreadsheet';
  if (m.includes('google-apps.presentation') || m.includes('presentation') || m.includes('ms-powerpoint')) return 'presentation';
  if (m.includes('google-apps.form')) return 'form';
  if (m.includes('google-apps.drawing')) return 'drawing';
  if (m === 'application/pdf') return 'pdf';
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.includes('zip') || m.includes('compressed') || m.includes('tar')) return 'archive';
  return kindFromFilename(name) ?? (m.startsWith('text/') ? 'document' : 'file');
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    return s;
  }
}

function lastSegmentFilename(pathname: string): string | null {
  const segs = pathname.split('/').filter(Boolean);
  const last = segs[segs.length - 1];
  if (!last) return null;
  const name = safeDecode(last);
  return kindFromFilename(name) ? name : null;
}

// SharePoint/OneDrive-for-Business sharing links: /:w:/r/…, /:x:/g/…, …
const SP_KIND: Record<string, CloudFileKind> = {
  w: 'document',
  x: 'spreadsheet',
  p: 'presentation',
  b: 'pdf',
  f: 'folder',
  i: 'image',
  v: 'video',
  o: 'document', // OneNote
  u: 'file',
  t: 'document',
};

function parseGoogle(url: URL): CloudLinkInfo | null {
  const host = url.hostname.toLowerCase();
  const path = url.pathname;
  const href = url.href;
  if (host === 'docs.google.com') {
    if (/^\/document\//.test(path)) return { provider: 'google_drive', kind: 'document', name: null, href };
    if (/^\/spreadsheets\//.test(path)) return { provider: 'google_drive', kind: 'spreadsheet', name: null, href };
    if (/^\/presentation\//.test(path)) return { provider: 'google_drive', kind: 'presentation', name: null, href };
    if (/^\/forms\//.test(path)) return { provider: 'google_drive', kind: 'form', name: null, href };
    if (/^\/drawings\//.test(path)) return { provider: 'google_drive', kind: 'drawing', name: null, href };
    if (/^\/file\//.test(path)) return { provider: 'google_drive', kind: 'file', name: null, href };
    return { provider: 'google_drive', kind: 'file', name: null, href };
  }
  if (host === 'drive.google.com') {
    if (/\/folders\//.test(path)) return { provider: 'google_drive', kind: 'folder', name: null, href };
    return { provider: 'google_drive', kind: 'file', name: null, href };
  }
  return null;
}

function parseMicrosoft(url: URL): CloudLinkInfo | null {
  const host = url.hostname.toLowerCase();
  const href = url.href;
  if (host === '1drv.ms' || host === 'onedrive.live.com' || host.endsWith('.onedrive.live.com')) {
    // 1drv.ms/w/s!… short links carry the kind letter in the first segment.
    const first = url.pathname.split('/').filter(Boolean)[0] || '';
    const kind = (host === '1drv.ms' && SP_KIND[first.toLowerCase()]) || kindFromFilename(lastSegmentFilename(url.pathname)) || 'file';
    return { provider: 'onedrive', kind, name: lastSegmentFilename(url.pathname), href };
  }
  if (host.endsWith('.sharepoint.com')) {
    // <tenant>-my.sharepoint.com is OneDrive for Business.
    const provider: CloudFileProvider = /-my\.sharepoint\.com$/.test(host) ? 'onedrive' : 'sharepoint';
    const spMatch = /^\/:([a-z]):\//i.exec(url.pathname);
    const fileParam = url.searchParams.get('file');
    const name = (fileParam && safeDecode(fileParam)) || lastSegmentFilename(url.pathname);
    let kind: CloudFileKind = spMatch ? SP_KIND[spMatch[1].toLowerCase()] ?? 'file' : 'file';
    if (kind === 'file') kind = kindFromFilename(name) ?? (/\/Forms\/AllItems\.aspx$/i.test(url.pathname) ? 'folder' : 'file');
    return { provider, kind, name, href };
  }
  return null;
}

/** Provider info for a Google Drive/Docs, OneDrive or SharePoint URL, else null. */
export function parseCloudLink(raw: string | null | undefined): CloudLinkInfo | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  return parseGoogle(url) ?? parseMicrosoft(url);
}

export function isCloudLink(raw: string | null | undefined): boolean {
  return parseCloudLink(raw) !== null;
}
